import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useReactToPrint } from 'react-to-print';
import dayjs from 'dayjs';

// MUI
import { DownloadRounded, UndoRounded } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

// common
import useService from 'common/hook/useService';
import useMonthlyMenuFrames from 'common/hook/useMonthlyMenuFrames';
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import NotFoundBox from 'common/components/NotFoundBox';
import SchemaForm from 'common/SchemaForm/';

// screen
import { ClientModel } from 'modules/map/types';
import MenuTable from 'modules/map/components/daily-consolidate-invoice/MenuTable';
import {
  DailyInvoiceFilterModel,
  DailyInvoiceModel,
  SettlementStatementOperationEnum,
} from 'modules/map/components/daily-consolidate-invoice/types';
import PdfDailyConsolidateInvoice from 'modules/map/components/daily-consolidate-invoice/pdf';

interface Props {
  clientData: ClientModel;
  open: boolean;
  onClose: () => void;
}

const DailyConsolidateInvoiceDialog = (props: Props) => {
  const { open, onClose, clientData } = props;
  const contentToPrint = useRef(null);
  const service = useService();

  // Get current date in Vietnam (UTC+7)
  const now = new Date();
  const vietnamOffset = 7 * 60; // Vietnam is UTC+7
  const vietnamTime = new Date(now.getTime() + vietnamOffset * 60 * 1000);

  // Create midnight Vietnam time and convert to ISO
  // This represents midnight Jan 30 Vietnam as Jan 30 00:00:00 UTC
  const year = vietnamTime.getUTCFullYear();
  const month = vietnamTime.getUTCMonth();
  const day = vietnamTime.getUTCDate();
  const midnightVietnamAsUTC = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));

  const [filter, setFilter] = useState<DailyInvoiceFilterModel>({
    state: SettlementStatementOperationEnum.confirm,
    date: midnightVietnamAsUTC.toISOString(),
  });
  const handlePrint = useReactToPrint({
    contentRef: contentToPrint,
    pageStyle: `
      @page { size: A4 portrait; margin: 1cm; }
    `,
  });

  const searchStr = useMemo(() => {
    return [dayjs(filter.date).format('YYYY-M-0-D'), filter.state].join('-');
  }, [filter.date, filter.state]);

  /**
   * Khoảng ngày cho phép chọn: từ đầu khung sớm nhất đến cuối khung muộn nhất của năm học.
   * Trước đây chặn theo NĂM DƯƠNG LỊCH (`day.year() !== dayjs().year()`) nên đứng ở tháng 12 sẽ
   * không chọn được ngày của học kỳ 2 — vốn nằm ở năm dương lịch sau.
   * Không tải được khung thì thôi không chặn, còn hơn khoá cứng cả màn hình.
   */
  const { frames } = useMonthlyMenuFrames();
  const schoolYearRange = useMemo(() => {
    if (frames.length === 0) return null;
    const starts = frames.map((f) => f.month_start).sort();
    const ends = frames.map((f) => f.month_end).sort();
    return { from: dayjs(starts[0]), to: dayjs(ends[ends.length - 1]) };
  }, [frames]);

  const { data, isFetching } = useQuery({
    queryKey: [searchStr, 'nutrition-menu'],
    queryFn: async () => {
      if (!service) return null;
      const json = await service.ClientDailyConsolidatedInvoiceController_list({
        params: {
          orgId: clientData.operationCode,
          mode: 'real',
        },
        queries: {
          limit: 50,
          offset: 0,
          searchStr: searchStr,
        },
      });

      const result = json?.[0] as unknown as DailyInvoiceModel;
      if (!result) return null;

      const menuDatetime = result.structuredData?.data?.menu?.datetime;
      if (!menuDatetime) return result;

      // Convert UTC+7 datetime to UTC by preserving the date/time values (immutable)
      // "2026-01-30T00:00:00+07:00" -> "2026-01-30T00:00:00.000Z"
      // Extract date/time parts and ignore the timezone offset
      const match = menuDatetime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        const [, year, month, day, hour, minute, second] = match;
        const cloned = structuredClone(result);
        cloned.structuredData.data.menu.datetime = `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`;
        return cloned;
      }

      return result;
    },
    enabled: open && !!service,
  });

  const renderTable = ({
    isPrintedTable = false,
    refProp = null,
  }: {
    isPrintedTable?: boolean;
    refProp?: React.Ref<HTMLDivElement>;
  }) => {
    const renderTableContent = () => {
      if (isFetching) return <MuiLoading />;
      if (!data) return <NotFoundBox />;
      if (!isPrintedTable) return <MenuTable data={data} />;

      return <PdfDailyConsolidateInvoice data={data} />;
    };

    return (
      <Box
        sx={{
          overflow: isPrintedTable ? 'hidden' : undefined,
          height: isPrintedTable ? 0 : '100%',
        }}
      >
        <Box ref={refProp} className="flex flex-col items-center justify-center w-full h-full">
          {renderTableContent()}
        </Box>
      </Box>
    );
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      title={'map.dailyInvoice'}
      maxWidth="desktop"
      fullWidth
      titleAlign="center"
      PaperProps={{
        sx: {
          margin: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
      disablePortal
    >
      <Box className="p-2 !pb-0 tablet:p-4 mb-2">
        <SchemaForm<DailyInvoiceFilterModel>
          formData={filter}
          onChange={setFilter}
          globalProps={{ noHelperText: true }}
          schema={{
            fields: ({ formProps: { isMobile, intl } }) => {
              return {
                dateText: {
                  mode: 'node',
                  hidden: isMobile,
                  render: () => {
                    return (
                      <Typography mt={1}>
                        <FormattedMessage id="map.date" />
                      </Typography>
                    );
                  },
                  propsWrapper: { mobile: undefined },
                },
                date: {
                  mode: 'datePicker',
                  label: isMobile ? <FormattedMessage id="map.date" /> : undefined,
                  shouldDisableDate: (day) => {
                    if (!schoolYearRange) return false;
                    return (
                      day.isBefore(schoolYearRange.from, 'day') ||
                      day.isAfter(schoolYearRange.to, 'day')
                    );
                  },
                  slotProps: {
                    popper: { disablePortal: true },
                  },
                  onChange: (value) => {
                    // Convert picked date to Vietnam timezone representation
                    // value can be Date, ISO string, or Day.js object depending on datepicker
                    if (!value) return value;
                    const d = dayjs(value);
                    const year = d.year();
                    const month = d.month();
                    const day = d.date();
                    const midnightVietnamAsUTC = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
                    return midnightVietnamAsUTC.toISOString();
                  },
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: { mobile: 6, tablet: 3.2 },
                },
                blank: {
                  mode: 'node',
                  hidden: isMobile,
                  propsWrapper: { mobile: true },
                },
                text: {
                  mode: 'node',
                  hidden: isMobile,
                  propsWrapper: { mobile: undefined },
                  render: () => {
                    return (
                      <Typography mt={1} variant="subtitle1">
                        <FormattedMessage id="status" />
                        :&nbsp;
                      </Typography>
                    );
                  },
                },
                state: {
                  label: isMobile && <FormattedMessage id="map.process" />,
                  mode: 'select',
                  options: [
                    {
                      id: SettlementStatementOperationEnum.confirm,
                      name: intl.formatMessage({ id: 'map.confirm' }),
                    },
                    {
                      id: SettlementStatementOperationEnum.complete,
                      name: intl.formatMessage({ id: 'map.completed' }),
                    },
                  ],
                  rawOptions: true,
                  disableClearBtn: true,
                  noHelperText: true,
                  propsWrapper: {
                    mobile: 6,
                    tablet: 2,
                    laptop: undefined,
                  },
                },
              };
            },
          }}
        />
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {renderTable({ isPrintedTable: true, refProp: contentToPrint })}
        {renderTable({})}
      </Box>
      <Box className="p-2 tablet:p-4 flex justify-end gap-2 tablet:gap-4">
        <Button
          startIcon={<DownloadRounded />}
          onClick={() => {
            handlePrint();
          }}
          disabled={!data}
        >
          <FormattedMessage id="map.download" />
        </Button>
        <Button variant="outlined" startIcon={<UndoRounded />} onClick={onClose}>
          <FormattedMessage id="back" />
        </Button>
      </Box>
    </MuiDialog>
  );
};

export default DailyConsolidateInvoiceDialog;
